import * as React from 'react';
import { WithRouterProps } from 'react-router';
import Helmet from 'react-helmet';

import { IntlProps, Language } from 'ha/i18n';
import { checkSearchQueryEmpty } from 'ha/utils/checkSearchQueryEmpty';
import { LocaleToCanonicalCollections } from 'ha/constants/SearchPageCollections';

interface OwnProps {
  langUrlList: { [key in Language]?: string };
  numberOfListings: number;
  canonicalCollection?: string;
  page?: string | number;
  advertiserName?: string;
}

type Props = OwnProps & IntlProps & WithRouterProps;

const MinNumOfListingsForIndex = 4;

const getCollectionPath = (key: Language, canonicalCollection?: string) =>
  canonicalCollection
    ? `/${LocaleToCanonicalCollections[key][canonicalCollection]}`
    : '';

const SearchPageCrawl: React.SFC<Props> = ({
  langUrlList,
  numberOfListings,
  page,
  location: { query, pathname },
  intl: { lang, urlResolver },
  canonicalCollection,
  advertiserName,
}) => {
  const isQueryEmpty = checkSearchQueryEmpty(query);
  const localeUrl = langUrlList[lang];
  const translatedPathname = `${localeUrl}${getCollectionPath(
    lang,
    canonicalCollection,
  )}`;
  const translatedPathnameIsDifferent =
    localeUrl &&
    decodeURIComponent(pathname) !== decodeURIComponent(translatedPathname);

  const setNoIndex =
    numberOfListings < MinNumOfListingsForIndex ||
    !isQueryEmpty ||
    translatedPathnameIsDifferent ||
    advertiserName;

  const pageQuery = page && page !== '1' ? `?page=${page}` : '';

  return setNoIndex ? (
    <Helmet>
      <meta name="robots" content="noindex" />
    </Helmet>
  ) : (
    <Helmet>
      <link
        rel="canonical"
        href={urlResolver.absolutizePath(`${translatedPathname}${pageQuery}`, {
          skipAddLanguage: true,
        })}
      />
      {langUrlList.en && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href={urlResolver.absolutizePath(
            `${langUrlList.en}${getCollectionPath(
              Language.en,
              canonicalCollection,
            )}${pageQuery}`,
            { skipAddLanguage: true },
          )}
        />
      )}
      {Object.keys(langUrlList).map(
        (key: Language) =>
          langUrlList[key] && (
            <link
              rel="alternate"
              key={key}
              hrefLang={key}
              href={urlResolver.absolutizePath(
                `${langUrlList[key]}${getCollectionPath(
                  key,
                  canonicalCollection,
                )}${pageQuery}`,
                { skipAddLanguage: true },
              )}
            />
          ),
      )}
    </Helmet>
  );
};

export default SearchPageCrawl;
